import { useEffect, useState, useCallback, useRef, useMemo, memo } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import { FormCustomLabel } from '@/components/formBuilder/components';
import { useMutation } from '@tanstack/react-query';
import { SchInterviewerFieldAutoRecommend } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerFieldAutoRecommend';
import { SchInterviewerFieldNoRecommend } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerFieldNoRecommend';
import debounce from 'debounce-promise';
// import { SchInterviewerSelectMenu } from '@/pages/KInterviewScheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerSelectMenu';
import { SchInterviewerOption } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerOption';
import { useField } from 'formik';
import { isEqual } from 'lodash-es';
import { SchInterviewerMultiValueLabel } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerMultiValueLabel';
import { SchInterviewerMultiValueContainer } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerMultiValueContainer';
import { InterviewDetailsAPI } from '@/components/shared/interview-scheduler/SchedulerDetails/api/InterviewDetailsAPI';
import AsyncSelect from 'react-select/async';
import { getCustomStyles } from '@/theme/components/select';
import { CenterSpinner } from '@/components/ats';
import { SchInterviewerSingleLabel } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerSingleLabel';
import { useSchInterviewerLogic } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/useSchInterviewerLogic';
import { KText } from '@/ui-library/components';
import { useShowToast } from '@/hooks';
import { SchInterviewerGroupHeading } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerGroupHeading';
import { PiSmileySadDuotone } from 'react-icons/pi';
import { components } from 'react-select';
import { IoMdClose } from 'react-icons/io';
import { COLORS } from '@/ui-library/design-system';
import { hexToRgbAdjusted } from '@/lib/util';
import { SchInterviewersInfo } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewersInfo';
import { SchInterviewerTimezoneModal } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerTimezoneModal';
import { colors } from '@/theme/foundations/colors';
import { normalizeTextForSearch } from '@/utils/commonHelpers';
import { KSelectStylesConfig } from '@/ui-library/design-system/constants/KSelect.styles';

interface Interviewer {
  id: string;
  name: string;
  [key: string]: any;
}

interface Option {
  value: string;
  label: string;
  [key: string]: any;
}

interface OptionGroup {
  label: string;
  options: Option[];
}

interface InterviewerData {
  data?: {
    pool_interviewers?: Interviewer[];
    interviewers?: Interviewer[];
  };
  errors?: string[];
  status?: number;
}

interface SchInterviewerFieldProps {
  formik: any;
  name?: string;
  interviewDataRef: React.MutableRefObject<any>;
  updateSharedData: (data: any) => void;
  registerForUpdates: (
    key: string,
    callback: (data: any) => void
  ) => () => void;
  interviewPlan: any;
  variant?: string;
  editMode?: boolean;
  jobStageActivityId: number;
  isAdhoc: boolean;
  isBulkManual?: boolean;
  styleVersion?: string;
}

type ColorVariant = { light: string; dark: string };

const getFormattedOptions = (
  options: InterviewerData | null,
  selected: Interviewer[],
  query: string = ''
): OptionGroup[] => {
  if (!options) return [];

  const allInterviewers =
    options?.data?.interviewers?.map((user) => ({
      ...user,
      value: user.id,
      label: user.name,
    })) || [];

  // Merge working hours and other latest data from allInterviewers into selected
  const selectedOptions = (
    query
      ? // eslint-disable-next-line no-unsafe-optional-chaining -- pre-existing; behavior preserved (extraction move)
        selected?.filter((user) => {
          const normalizedUserName = normalizeTextForSearch(user?.name);
          const normalizedQuery = normalizeTextForSearch(query);
          return normalizedUserName.includes(normalizedQuery);
        })
      : selected
  ).map((user) => {
    const latest = allInterviewers.find((i) => i.id === user.id);
    return latest ? { ...user, ...latest } : user;
  });

  // Filter interviewers based on query
  const filteredInterviewers = query
    ? allInterviewers.filter((user) => {
        const normalizedUserName = normalizeTextForSearch(user.name);
        const normalizedQuery = normalizeTextForSearch(query);
        return normalizedUserName.includes(normalizedQuery);
      })
    : allInterviewers;

  return [
    {
      label: 'SELECTED',
      options:
        selectedOptions?.map((user) => ({
          ...user,
          value: user?.id,
          label: user?.name,
        })) || [],
    },
    {
      label: 'Interviewers',
      options: filteredInterviewers,
    },
  ];
};

const schInterviewerFieldColorMap: Record<number, string> = {};

export const SchInterviewerField: React.FC<SchInterviewerFieldProps> = memo(
  ({
    formik,
    name = 'interviewers',
    interviewDataRef,
    updateSharedData,
    registerForUpdates,
    interviewPlan,
    variant = 'manual',
    editMode = false,
    jobStageActivityId,
    isAdhoc = false,
    isBulkManual = false,
    styleVersion = 'v1',
  }) => {
    const [field, meta, helpers] = useField(name);
    const { value, error, touched, initialValue } = meta;
    const { setTouched, setValue, setError } = helpers;
    const submitCount = formik?.submitCount || 0;
    const isError =
      (touched || submitCount > 0) &&
      error &&
      (Array.isArray(value) ? value?.length === 0 : !value);

    const [, { value: kindValue }] = useField('kind');
    const isMulti = ['manual', 'bulk-manual'].includes(variant)
      ? kindValue === 'panel'
      : true;
    const prevIsMultiRef = useRef<boolean>(isMulti);

    const [selectInput, setSelectInput] = useState('');

    const [optionsCache, setOptionsCache] = useState<
      Record<string, InterviewerData | null>
    >({});
    const [selectOption, setSelectOption] = useState<Option[]>([]);

    // Maintain a persistent color map across renders

    const toast = useShowToast();

    const {
      datesSet,
      interviewers: selectedInterviewers,
      bookedSlot,
      currentWeek,
      interviewKind,
    } = interviewDataRef?.current ?? {};

    const [localBookedSlot, setLocalBookedSlot] = useState(bookedSlot);
    const [localCurrentWeek, setLocalCurrentWeek] = useState(currentWeek);
    const [localDatesSet, setLocalDatesSet] = useState(datesSet);
    const [localInterviewKind, setLocalInterviewKind] = useState(interviewKind);
    const [
      selectedInterviewerFromTemplate,
      setSelectedInterviewerFromTemplate,
    ] = useState(interviewPlan?.interviewers || []);
    const availabilityUseDisclosure = useDisclosure();

    const SELF_SCHEDULE_VARIANTS = {
      SELF: 'self',
      BULK_SELF: 'bulk-self',
    };

    // Timezone modal state
    const timezoneModalDisclosure = useDisclosure();

    // Get all interviewers without timezone from current selection
    const interviewersWithoutTimezone = useMemo(() => {
      return selectOption
        .filter((option) => !option.time_zone)
        .map((option) => ({
          id: Number(option.value),
          name: option.label || option.name,
          avatar: option.avatar,
        }));
    }, [selectOption]);

    const handleTimezoneClick = useCallback(() => {
      timezoneModalDisclosure.onOpen();
    }, [timezoneModalDisclosure]);

    // Track timezone modal trigger from parent - opens modal when trigger changes
    const lastTimezoneModalTriggerRef = useRef(0);
    const currentTrigger = interviewDataRef?.current?.timezoneModalTrigger ?? 0;
    if (
      currentTrigger > 0 &&
      currentTrigger !== lastTimezoneModalTriggerRef.current
    ) {
      lastTimezoneModalTriggerRef.current = currentTrigger;
      // Schedule modal open after render to avoid side effects during render
      setTimeout(() => timezoneModalDisclosure.onOpen(), 0);
    }

    const handleTimezoneUpdated = useCallback(
      (updatedInterviewers: { id: number; time_zone: string }[]) => {
        // Update the selectOption with the new timezone
        setSelectOption((prev) => {
          const updatedOptions = prev.map((option) => {
            const updated = updatedInterviewers?.find(
              (u) => u.id === Number(option.value)
            );
            if (updated) {
              return { ...option, time_zone: updated.time_zone };
            }
            return option;
          });

          // Also update shared data immediately to ensure Scheduler sees the changes
          if (updatedOptions?.length > 0) {
            updateSharedData({ interviewersDetails: updatedOptions });
          }

          return updatedOptions;
        });
      },
      [updateSharedData]
    );

    // this is for when using find time button
    const noSlotsAvailableUseDisclosure = useDisclosure();

    const { startStr: weekStartDate } = localDatesSet || {
      startStr: interviewDataRef?.current?.datesSet?.startStr,
    };

    useEffect(() => {
      const unregisterInterviewers = registerForUpdates(
        'bookedSlot',
        (data) => {
          const { bookedSlot } = data?.fullData ?? {};
          setLocalBookedSlot((prev) =>
            isEqual(prev, bookedSlot) ? prev : bookedSlot
          );
        }
      );

      const unregisterCurrentWeek = registerForUpdates(
        'currentWeek',
        (data) => {
          const { currentWeek } = data?.fullData ?? {};
          setLocalCurrentWeek((prev) =>
            isEqual(prev, currentWeek) ? prev : currentWeek
          );
        }
      );

      const unregisterWeekAvailability = registerForUpdates(
        'weekAvailability',
        (data) => {
          const { weekAvailability, currentWeek, interviewers } =
            data?.fullData ?? {};
          if (weekAvailability?.[currentWeek] === false) {
            if (
              !editMode && Array.isArray(interviewers)
                ? interviewers?.length > 0
                : false
            ) {
              availabilityUseDisclosure?.onOpen();
            }
          } else {
            availabilityUseDisclosure?.onClose();
          }
        }
      );

      const unregisterNoSlots = registerForUpdates(
        'noSlotsAvailable',
        (data) => {
          const { noSlotsAvailable } = data?.fullData ?? {};
          if (noSlotsAvailable) {
            noSlotsAvailableUseDisclosure?.onOpen();
          } else {
            noSlotsAvailableUseDisclosure?.onClose();
          }
        }
      );

      const unregisterDatesSet = registerForUpdates('datesSet', (data) => {
        const { datesSet } = data?.fullData ?? {};
        setLocalDatesSet((prev) => (isEqual(prev, datesSet) ? prev : datesSet));
      });

      const unregisterInterviewKind = registerForUpdates(
        'interviewKind',
        (data) => {
          const { interviewKind } = data?.fullData ?? {};
          setLocalInterviewKind((prev) =>
            isEqual(prev, interviewKind) ? prev : interviewKind
          );
        }
      );

      return () => {
        unregisterWeekAvailability();
        unregisterInterviewKind();
        unregisterInterviewers();
        unregisterCurrentWeek();
        unregisterDatesSet();
        unregisterNoSlots();
      };
    }, [
      availabilityUseDisclosure,
      editMode,
      noSlotsAvailableUseDisclosure,
      registerForUpdates,
    ]);

    useEffect(() => {
      const options =
        interviewPlan?.participants?.map((user) => ({
          ...user,
          label: user.name,
          value: user.id,
        })) || [];
      if (initialValue) {
        setSelectOption(options);
      }
    }, [initialValue, setSelectOption, interviewPlan]);

    const enableRecommendation = useMemo(
      () =>
        !value &&
        !editMode &&
        variant === 'manual' &&
        isAdhoc === false &&
        localInterviewKind === 'one_on_one',
      [value, editMode, variant, isAdhoc, localInterviewKind]
    );

    const recommendedData = useSchInterviewerLogic({
      enableRecommendation,
      interviewPlan,
      setValue,
      datesSet,
      bookedSlot: localBookedSlot,
      jobStageActivityId,
    });

    // Update the fetchInterviewers mutation to store data with weekStartDate and query as key
    const fetchInterviewers = useMutation<
      InterviewerData,
      Error,
      { inputValue: string }
    >({
      mutationFn: async ({ inputValue }) => {
        const response = await InterviewDetailsAPI.getInterviewersList({
          startDate: weekStartDate,
          q: inputValue,
        });
        return response || {};
      },
      onSuccess: (data, { inputValue }) => {
        const cacheKey = `${weekStartDate}-${inputValue}`;
        setOptionsCache((prev) => ({
          ...prev,
          [cacheKey]: data, // Cache data with weekStartDate and query as key
        }));

        // Save to localStorage only if in bulk manual mode and conditions met
        if (isBulkManual && weekStartDate && jobStageActivityId) {
          const cacheKey = `interviewerOptionsCache_${jobStageActivityId}_${weekStartDate}`;
          try {
            localStorage.setItem(cacheKey, JSON.stringify(data));
          } catch (error) {
            console.error(
              '[SchInterviewerField] Error saving to localStorage:',
              error
            );
          }
        }

        // Update all selected interviewers with availability data
        setSelectedInterviewerFromTemplate((prev) => {
          const res = prev.map((selected) => {
            const currentUser = data?.data?.interviewers?.find(
              (user) => user?.id === selected?.id
            );
            if (
              currentUser?.week_interview_hours !== undefined &&
              currentUser?.week_interview_hours_limit !== undefined
            ) {
              return {
                ...selected,
                week_interview_hours: currentUser.week_interview_hours,
                week_interview_hours_limit:
                  currentUser.week_interview_hours_limit,
              };
            }
            return selected; // Return unchanged if no availability data
          });
          return res;
        });
      },
      onError: (error) => {
        console.error('Failed to fetch interviewers:', error);
        setError('Failed to load interviewers');
      },
      onSettled(data) {
        const { errors } = data || {};
        if (errors && errors?.[0]) {
          toast({
            title: 'Failed to load interviewers',
            status: 'error',
            description:
              typeof errors?.[0] === 'string'
                ? errors?.[0]
                : 'Something went wrong',
          });
        }
      },
    });

    // Create a stable debounced fetch function using useRef to prevent recreation
    const debouncedFetchRef = useRef<
      ((inputValue: string) => Promise<OptionGroup[]>) | null
    >(null);

    // Initialize the debounced function only once
    if (!debouncedFetchRef.current) {
      debouncedFetchRef.current = debounce(
        async (inputValue: string): Promise<OptionGroup[]> => {
          try {
            const response = await fetchInterviewers.mutateAsync({
              inputValue,
            });

            // Create the updated array
            const updatedSelected = selectedInterviewerFromTemplate.map(
              (selected) => {
                const currentUser = response?.data?.interviewers?.find(
                  (user) => user?.id === selected?.id
                );
                if (
                  currentUser?.week_interview_hours !== undefined &&
                  currentUser?.week_interview_hours_limit !== undefined
                ) {
                  return {
                    ...selected,
                    week_interview_hours: currentUser.week_interview_hours,
                    week_interview_hours_limit:
                      currentUser.week_interview_hours_limit,
                  };
                }
                return selected;
              }
            );

            // Update state
            setSelectedInterviewerFromTemplate(updatedSelected);

            // Use the updated array here!
            const formattedOptions = getFormattedOptions(
              response,
              updatedSelected,
              inputValue
            );
            return formattedOptions;
          } catch (e) {
            console.error('[SchInterviewerField] debouncedFetch error:', e);
            return [];
          }
        },
        300
      );
    }

    // Wrap in useCallback to maintain stable function reference
    const loadOptions = useCallback(
      async (inputValue: string): Promise<OptionGroup[]> => {
        const cacheKey = `${weekStartDate}-${inputValue}`;
        const cachedData = optionsCache[cacheKey];

        if (cachedData) {
          // Use cached data if available
          const formattedOptions = getFormattedOptions(
            cachedData,
            selectedInterviewerFromTemplate,
            inputValue
          );
          return formattedOptions;
        } else {
          // Fetch from API if not cached - this will be debounced
          return debouncedFetchRef.current!(inputValue);
        }
      },
      [weekStartDate, optionsCache, selectedInterviewerFromTemplate]
    );

    // Note: debounce-promise doesn't have a cancel method like lodash debounce  so remove it
    // The debounced function will automatically handle cleanup

    // Initial fetch only when optionsCache is null and startDate exists
    // Initial fetch and refetch when weekStartDate changes
    useEffect(() => {
      // Only fetch if weekStartDate exists and we don't have cached data for this specific weekStartDate
      const cacheKey = `${weekStartDate}-`;
      if (
        weekStartDate &&
        !optionsCache[cacheKey] && // Check cache for this specific weekStartDate with empty query
        !fetchInterviewers.isPending // Prevent fetch if already in progress
      ) {
        fetchInterviewers.mutate({ inputValue: '' });
      }
      // eslint-disable-next-line @tanstack/query/no-unstable-deps -- pre-existing; behavior preserved (extraction move)
    }, [weekStartDate, optionsCache, fetchInterviewers]);

    // Sync initial values
    useEffect(() => {
      const cacheKey = `${weekStartDate}-`;
      if (
        optionsCache &&
        optionsCache[cacheKey] &&
        field.value &&
        selectOption?.length === 0
      ) {
        const allInterviewers = [
          ...(optionsCache[cacheKey]?.data?.pool_interviewers || []),
          ...(optionsCache[cacheKey]?.data?.interviewers || []),
        ];
        let initialValues: Option[] = [];
        if (isMulti && Array.isArray(field.value)) {
          initialValues = field.value
            .map((id) => {
              const interviewer =
                allInterviewers.find(
                  (user) => String(user.id) === String(id)
                ) ||
                (selectedInterviewerFromTemplate as any[])?.find(
                  (user) => String(user.id) === String(id)
                );
              return interviewer
                ? {
                    ...interviewer,
                    value: interviewer.id,
                    label: interviewer.name,
                  }
                : null;
            })
            .filter(Boolean) as Option[];
        } else {
          const singleId = Array.isArray(field.value)
            ? field.value?.[0]
            : field.value;
          if (Array.isArray(field.value)) {
            setValue(singleId ?? null);
          }
          if (singleId !== undefined && singleId !== null) {
            const interviewer =
              allInterviewers.find(
                (user) => String(user.id) === String(singleId)
              ) ||
              (selectedInterviewerFromTemplate as any[])?.find(
                (user) => String(user.id) === String(singleId)
              );
            if (interviewer) {
              initialValues = [
                {
                  ...interviewer,
                  value: interviewer.id,
                  label: interviewer.name,
                },
              ];
            }
          }
        }
        setSelectOption(initialValues);
      } else if (field.value && selectOption?.length > 0) {
        // Added logic to prevent unnecessary resetting of selectOption during re-renders
        // Ensures selectOption stays in sync with field.value only if there's a mismatch
        // This prevents the UI from losing selections when other form fields cause re-renders
        const currentValues =
          isMulti && Array.isArray(field.value)
            ? field.value
            : field.value
              ? [field.value]
              : [];
        const selectValues = selectOption.map((opt) => opt.value);
        if (!isEqual(currentValues.sort(), selectValues.sort())) {
          const allInterviewers = optionsCache[cacheKey]
            ? [
                ...(optionsCache[cacheKey]?.data?.pool_interviewers || []),
                ...(optionsCache[cacheKey]?.data?.interviewers || []),
              ]
            : [];
          let updatedValues: Option[] = [];
          if (isMulti && Array.isArray(field.value)) {
            updatedValues = field.value
              .map((id) => {
                const interviewer =
                  allInterviewers.find(
                    (user) => String(user.id) === String(id)
                  ) ||
                  (selectedInterviewerFromTemplate as any[])?.find(
                    (user) => String(user.id) === String(id)
                  );
                return interviewer
                  ? {
                      ...interviewer,
                      value: interviewer.id,
                      label: interviewer.name,
                    }
                  : null;
              })
              .filter(Boolean) as Option[];
          } else {
            const singleId = Array.isArray(field.value)
              ? field.value?.[0]
              : field.value;
            if (Array.isArray(field.value)) {
              setValue(singleId ?? null);
            }
            if (singleId !== undefined && singleId !== null) {
              const interviewer =
                allInterviewers.find(
                  (user) => String(user.id) === String(singleId)
                ) ||
                (selectedInterviewerFromTemplate as any[])?.find(
                  (user) => String(user.id) === String(singleId)
                );
              if (interviewer) {
                updatedValues = [
                  {
                    ...interviewer,
                    value: interviewer.id,
                    label: interviewer.name,
                  },
                ];
              }
            }
          }
          setSelectOption(updatedValues);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      field.value,
      optionsCache,
      selectOption?.length,
      isMulti,
      weekStartDate,
    ]);

    // Handle switch from multi to single mode
    useEffect(() => {
      if (prevIsMultiRef.current && !isMulti && selectOption.length > 1) {
        // From multi to single: Keep only the first value
        const firstValue = selectOption[0];
        setSelectOption([firstValue]);
        setValue(firstValue.value); // Update Formik field to single value
      }
      prevIsMultiRef.current = isMulti; // Update ref after check
    }, [isMulti, selectOption, setValue]);

    useEffect(() => {
      if (isEqual(selectedInterviewers, value)) return;

      const currentValue =
        isMulti && Array.isArray(value) ? value : value ? [value] : [];

      // Build color codes for each interviewer
      const colorCodes: Record<string, ColorVariant> = {};

      // Only when candidate availability is present: exclude pastel-2 from interviewer colors.
      const hasCandidateAvailability =
        (interviewDataRef.current?.candidateAvailabilitySlots?.length ?? 0) > 0;
      const pastelIndicesWithout2 = [1, 3, 4, 5, 6, 7, 8, 9, 10];

      currentValue.forEach((id, index) => {
        const idNum = Number(id);
        const existingColorKey = schInterviewerFieldColorMap[idNum];
        const mustSkipPastel2 =
          hasCandidateAvailability && existingColorKey === 'pastel-2';
        const colorKey =
          existingColorKey && !mustSkipPastel2
            ? existingColorKey
            : hasCandidateAvailability
              ? `pastel-${pastelIndicesWithout2[index % pastelIndicesWithout2.length]}`
              : `pastel-${(index % 10) + 1}`;
        const color = COLORS[colorKey as keyof typeof COLORS] as {
          light: string;
          dark: string;
        };

        colorCodes[idNum] = {
          light: hexToRgbAdjusted(color.light, 0.5),
          dark: hexToRgbAdjusted(color.dark, 0.5),
        };
        schInterviewerFieldColorMap[idNum] = colorKey;
      });

      // Update shared data
      updateSharedData({
        interviewers: currentValue,
        colorCodes,
        ...(selectOption?.length > 0 && { interviewersDetails: selectOption }),
      });
    }, [
      value,
      selectedInterviewers,
      updateSharedData,
      isMulti,
      selectOption,
      interviewDataRef,
    ]);

    const handleChange = useCallback(
      (selectedOptions: Option | Option[] | null) => {
        const newValues = isMulti
          ? (selectedOptions as Option[])?.map((option) => option.value) || []
          : (selectedOptions as Option)?.value || null;

        const newSelectedOptions = isMulti
          ? (selectedOptions as Option[]) || []
          : selectedOptions
            ? [selectedOptions as Option]
            : [];

        setSelectOption(newSelectedOptions);
        setTouched(true);
        setError(null);
        setValue(newValues);
      },
      [isMulti, setTouched, setError, setValue]
    );

    // Update defaultOptions to use the current search input and cache
    const getDefaultOptions = useCallback(() => {
      const cacheKey = `${weekStartDate}-${selectInput}`;
      const cachedData = optionsCache[cacheKey];

      if (cachedData) {
        return getFormattedOptions(
          cachedData,
          selectedInterviewerFromTemplate,
          selectInput
        );
      }

      // Fallback to empty query cache if available
      const emptyQueryCacheKey = `${weekStartDate}-`;
      const emptyQueryData = optionsCache[emptyQueryCacheKey];

      if (emptyQueryData) {
        return getFormattedOptions(
          emptyQueryData,
          selectedInterviewerFromTemplate,
          selectInput
        );
      }

      return [];
    }, [
      weekStartDate,
      selectInput,
      optionsCache,
      selectedInterviewerFromTemplate,
    ]);

    const defaultOptions = getDefaultOptions();

    const isRecommendationLoading = recommendedData?.isLoading;

    const isLoading = fetchInterviewers.isPending;

    // Array format for getCustomStyles
    const customStylesV1 = [
      {
        type: 'multiValueRemove',
        newStyles: {
          '&:hover': { background: 'inherit', color: 'secondary-700' },
          paddingLeft: '2px',
        },
      },
      {
        type: 'multiValueLabel',
        newStyles: { maxWidth: 'calc(100% - 20px)' },
      },
      {
        type: 'multiValue',
        newStyles: { border: 'none', borderRadius: '6px' },
      },
      {
        type: 'groupHeading',
        newStyles: {
          paddingRight: '0px',
        },
      },
      {
        type: 'control',
        newStyles: {
          ...(isError && { backgroundColor: '#FFEBE5' }),
        },
      },
      {
        type: 'placeholder',
        newStyles: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    ];

    // Object format for KSelectStylesConfig
    const customStylesV2 = {
      multiValueRemove: (provided) => ({
        ...provided,
        '&:hover': { background: 'inherit', color: 'secondary-700' },
        paddingLeft: '2px',
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        maxWidth: 'calc(100% - 20px)',
      }),
      multiValue: (provided) => ({
        ...provided,
        border: 'none',
        borderRadius: '6px',
        padding: '0px !important',
      }),
      groupHeading: (provided) => ({
        ...provided,
        paddingRight: '0px',
      }),
      control: (provided, state) => ({
        ...provided,
        boxShadow: 'none',
        '&:hover': {},
        '&:focus-within': {
          backgroundColor: state.selectProps?.hasError
            ? '#FFEBE5'
            : state.isDisabled
              ? COLORS.secondary[25]
              : COLORS.secondary[25],
          color: state.selectProps?.hasError
            ? ` #C03744 !important`
            : COLORS?.['secondary-700'],
          borderColor: state.selectProps?.hasError
            ? ' #C03744'
            : state.isDisabled
              ? COLORS.secondary[100]
              : COLORS.secondary[300],
        },
        ...(isError && {
          border: `1px solid ${colors['error-200']}`,
        }),
      }),
    };

    const styles = KSelectStylesConfig({
      variant: 'default',
      customStyles: customStylesV2,
    });

    const styleVersionMap = {
      v1: getCustomStyles(customStylesV1),
      v2: styles,
    };

    const selectStyles = styleVersionMap[styleVersion] || styles;

    return (
      <FormControl isRequired isInvalid={isError} margin="0.75em 0">
        <FormCustomLabel
          item={{
            label: 'Interviewer',
            name: 'interviewers',
            labelStyles: { position: 'relative' },
          }}
        />
        {(availabilityUseDisclosure?.isOpen ||
          noSlotsAvailableUseDisclosure?.isOpen) && (
          <Box
            position="fixed"
            right="29%"
            maxW="310px"
            mr="12px"
            top="45%"
            transform="translateY(-50%)"
            bg="white"
            boxShadow="0px 2px 8px rgba(0, 0, 0, 0.1)"
            borderRadius="6px"
            p="12px"
            zIndex={5}
          >
            <Flex flexDir="column" gap="6px" position="relative">
              <Flex gap="6px" alignItems="center">
                <Icon
                  w="20px"
                  h="20px"
                  // @ts-expect-error -- pre-existing type debt (extraction move)
                  as={PiSmileySadDuotone}
                  color="secondary-700"
                />
                <KText variant="heading_4" fontSize="sm">
                  {noSlotsAvailableUseDisclosure?.isOpen
                    ? 'No slots available'
                    : 'No slots available this week'}
                </KText>
              </Flex>
              <KText fontSize="xs">
                Try a different{' '}
                {noSlotsAvailableUseDisclosure?.isOpen ? 'time period' : 'week'}{' '}
                or replace interviewers to find open time slots
              </KText>
              <Icon
                // @ts-expect-error -- pre-existing type debt (extraction move)
                as={IoMdClose}
                w="16px"
                h="16px"
                position="absolute"
                top="4px"
                right="4px"
                color="gray.500"
                cursor="pointer"
                onClick={() => {
                  availabilityUseDisclosure?.onClose();
                  noSlotsAvailableUseDisclosure?.onClose();
                }}
              />
            </Flex>
          </Box>
        )}

        {isRecommendationLoading && (
          <Box
            position="absolute"
            top="20px"
            left="0"
            width="100%"
            height="calc(100% - 20px)"
            bg="rgba(248, 249, 252, 0.5)"
            zIndex="10"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="not-allowed"
          >
            <CenterSpinner spinnerProps={{ color: colors['primary-400'] }} />
          </Box>
        )}

        {!value && enableRecommendation && (
          <SchInterviewerFieldAutoRecommend
            setSelectOption={setSelectOption}
            setValue={setValue}
            recommendedData={recommendedData}
          />
        )}
        <AsyncSelect
          inputId="interviewers"
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          loadingMessage={() => <CenterSpinner h="100px" />}
          isLoading={isLoading}
          value={selectOption}
          isMulti={isMulti}
          isClearable={false}
          onInputChange={(inputText) => setSelectInput(inputText)}
          styles={selectStyles}
          hasError={isError}
          components={{
            DropdownIndicator: null,
            IndicatorSeparator: null,
            // Menu: (props) => (
            //   <SchInterviewerSelectMenu
            //     viewTitle={localCurrentWeek}
            //     {...props}
            //   />
            // ),
            GroupHeading: (props) => (
              <SchInterviewerGroupHeading
                viewTitle={localCurrentWeek}
                {...props}
              />
            ),
            MultiValueContainer: (props) => (
              <SchInterviewerMultiValueContainer
                {...props}
                registerForUpdates={registerForUpdates}
                interviewDataRef={interviewDataRef}
              />
            ),
            SingleValue: (props) => (
              <SchInterviewerSingleLabel
                {...props}
                registerForUpdates={registerForUpdates}
                interviewDataRef={interviewDataRef}
                variant={variant}
                SELF_SCHEDULE_VARIANTS={SELF_SCHEDULE_VARIANTS}
                onTimezoneClick={handleTimezoneClick}
              />
            ),
            MultiValueLabel: (props) => (
              <SchInterviewerMultiValueLabel
                {...props}
                interviewDataRef={interviewDataRef}
                registerForUpdates={registerForUpdates}
                variant={variant}
                SELF_SCHEDULE_VARIANTS={SELF_SCHEDULE_VARIANTS}
                onTimezoneClick={handleTimezoneClick}
              />
            ),
            MultiValueRemove: (props) => {
              return (
                <components.MultiValueRemove {...props}>
                  {/* @ts-expect-error -- pre-existing type debt */}
                  <Icon w="18px" h="18px" as={IoMdClose} />
                </components.MultiValueRemove>
              );
            },
            Option: SchInterviewerOption,
          }}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          placeholder="Select interviewer"
        />
        <FormErrorMessage flexDir="column" alignItems="flex-start">
          <KText color="ats-negative">{error}</KText>
        </FormErrorMessage>
        {!value && enableRecommendation && (
          <SchInterviewerFieldNoRecommend recommendedData={recommendedData} />
        )}
        {(variant === 'self' || variant === 'bulk-self') &&
          interviewKind === 'one_on_one' &&
          value?.length > 1 && (
            <Flex
              flexDir="column"
              gap="8px"
              mt="12px"
              bgColor="secondary-50"
              borderRadius="6px"
              p="12px"
              pr="5px"
            >
              <KText variant="heading_4">
                Multiple interviewers in one-on-one interviews
              </KText>
              <KText>
                Since you are sharing the availability of multiple interviewers,
                one interviewer will be selected based on the time slot picked
                by the candidate.
              </KText>
            </Flex>
          )}
        <SchInterviewersInfo
          variant={variant}
          interviewKind={interviewKind}
          value={value}
          bookedSlot={bookedSlot}
          localInterviewers={value}
        />
        {timezoneModalDisclosure.isOpen &&
          interviewersWithoutTimezone.length > 0 && (
            <SchInterviewerTimezoneModal
              isOpen={timezoneModalDisclosure.isOpen}
              onClose={timezoneModalDisclosure.onClose}
              interviewersWithoutTimezone={interviewersWithoutTimezone}
              onTimezoneUpdated={handleTimezoneUpdated}
            />
          )}
      </FormControl>
    );
  }
);

SchInterviewerField.displayName = 'SchInterviewerField';
